<section class="rcl" data-testid="runner-case-list" [attr.aria-label]="'testRunner.cases.aria' | translate">
  <header class="rcl__head">
    <h2 class="rcl__title">{{ 'testRunner.cases.title' | translate }}</h2>
    <span class="rcl__progress" data-testid="runner-case-progress">
      {{ 'testRunner.cases.progress' | translate: { done: done(), total: views().length } }}
    </span>
  </header>

  @if (views().length === 0) {
    <p class="rcl__empty" data-testid="runner-case-empty">{{ 'testRunner.cases.empty' | translate }}</p>
  } @else {
    <div class="rcl__columns" aria-hidden="true">
      <span class="rcl__col rcl__col--toggle"></span>
      <span class="rcl__col rcl__col--key">{{ 'testRunner.cases.column.key' | translate }}</span>
      <span class="rcl__col rcl__col--title">{{ 'testRunner.cases.column.case' | translate }}</span>
      <span class="rcl__col rcl__col--state">{{ 'testRunner.cases.column.state' | translate }}</span>
      <span class="rcl__col rcl__col--duration">{{ 'testRunner.cases.column.duration' | translate }}</span>
      <span class="rcl__col rcl__col--runner">{{ 'testRunner.cases.column.runner' | translate }}</span>
    </div>
    <ul class="rcl__list">
      @for (view of views(); track view.id) {
        <li
          class="rcl-case"
          data-testid="runner-case-row"
          [attr.data-case-id]="view.id"
          [class.rcl-case--current]="view.state.running"
          [class.rcl-case--pending]="!view.state.done && !view.state.running"
        >
          <div class="rcl-case__row">
            <span class="rcl__col rcl__col--toggle">
              <button
                type="button"
                class="rcl-case__toggle"
                data-testid="runner-case-toggle"
                [attr.aria-expanded]="isExpanded(view)"
                [attr.aria-controls]="stepsId(view)"
                [attr.aria-label]="'testRunner.cases.toggle' | translate: { title: view.title }"
                (click)="toggle(view)"
              >
                <mat-icon aria-hidden="true">{{ isExpanded(view) ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
            </span>
            <code class="rcl__col rcl__col--key rcl-case__key" data-testid="runner-case-key">{{ view.key }}</code>
            <span class="rcl__col rcl__col--title rcl-case__title" data-testid="runner-case-title">
              {{ view.title }}
            </span>
            <span class="rcl__col rcl__col--state" data-testid="runner-case-state">
              <app-res-pill
                size="sm"
                [status]="view.state.pill.status"
                [label]="view.state.pill.label | translate"
                [pulsing]="view.state.pill.pulsing"
              />
            </span>
            <span class="rcl__col rcl__col--duration" data-testid="runner-case-duration">{{ view.duration }}</span>
            <span class="rcl__col rcl__col--runner" data-testid="runner-case-runner">{{ view.runnerName }}</span>
          </div>
          @if (view.state.error; as error) {
            <p class="rcl-case__error" data-testid="runner-case-error">{{ error }}</p>
          }
          <div class="rcl-steps" data-testid="runner-case-steps" [id]="stepsId(view)" [hidden]="!isExpanded(view)">
            @if (view.current; as current) {
              <p class="rcl-steps__path" data-testid="runner-case-step-path">
                <span class="sr-only">{{ 'testRunner.cases.currentPathLabel' | translate }}:</span>
                {{ currentPath(view, current) }}
              </p>
            }
            @if (view.steps.length === 0) {
              <p class="rcl-steps__empty">{{ 'testRunner.cases.steps.empty' | translate }}</p>
            } @else {
              <table class="rcl-steps__table">
                <caption class="sr-only">
                  {{ 'testRunner.cases.steps.aria' | translate: { title: view.title } }}
                </caption>
                <thead>
                  <tr>
                    <th class="rcl-steps__col rcl-steps__col--nr" scope="col">
                      {{ 'testRunner.cases.steps.column.nr' | translate }}
                    </th>
                    <th class="rcl-steps__col rcl-steps__col--name" scope="col">
                      {{ 'testRunner.cases.steps.column.step' | translate }}
                    </th>
                    <th class="rcl-steps__col rcl-steps__col--result" scope="col">
                      {{ 'testRunner.cases.steps.column.result' | translate }}
                    </th>
                    <th class="rcl-steps__col rcl-steps__col--duration" scope="col">
                      {{ 'testRunner.cases.steps.column.duration' | translate }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (step of view.steps; track step.id) {
                    <tr
                      class="rcl-steps__row rcl-steps__row--level-{{ step.level > 4 ? 4 : step.level }}"
                      [attr.data-testid]="rowTestId(step)"
                      [class.rcl-steps__row--group]="!step.pill"
                      [class.rcl-steps__row--child]="step.depth > 0"
                      [class.rcl-steps__row--pass]="step.kind === 'pass' || step.kind === 'returnPass'"
                      [class.rcl-steps__row--call]="step.kind === 'call'"
                    >
                      <th class="rcl-steps__col rcl-steps__col--nr" scope="row" data-testid="runner-case-step-nr">
                        {{ step.number }}
                      </th>
                      <td class="rcl-steps__col rcl-steps__col--name" data-testid="runner-case-step-title">
                        @switch (step.kind) {
                          @case ('pass') {
                            {{ passLabelOf(step) }}
                          }
                          @case ('returnPass') {
                            {{ passLabelOf(step) }}
                          }
                          @case ('return') {
                            {{ passLabelOf(step) }}
                          }
                          @case ('call') {
                            {{ step.title }} –
                            <span data-testid="call-skipped-reason">{{ reasonOf(step.step) }}</span>
                          }
                          @default {
                            {{ step.title }}
                          }
                        }
                      </td>
                      <td class="rcl-steps__col rcl-steps__col--result">
                        @if (step.pill; as pill) {
                          <app-res-pill
                            size="sm"
                            [status]="pill.status"
                            [label]="pill.label | translate"
                            [pulsing]="pill.pulsing"
                          />
                        }
                      </td>
                      <td class="rcl-steps__col rcl-steps__col--duration" data-testid="runner-case-step-duration">
                        {{ step.duration }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            }
          </div>
        </li>
      }
    </ul>
  }
</section>
